/**
 * Who still works here.
 *
 * One definition, because the answer decides who appears on a roster, a report and a picker,
 * and three surfaces that each decide it for themselves will eventually disagree about the same
 * person. This started as a private helper on Leave Reports; the Shift Roster needed the same
 * question answered and copying it would have been the drift.
 *
 * ── The two ways somebody stops being current ─────────────────────────────────────────────
 *
 * `dateOfExit` is what User Management writes when somebody leaves, and what leaveAccrualEngine
 * already reads to stop accruing for them. Cognito's `enabled` flag covers the other route: an
 * account switched off without an exit date ever being filled in. Either one means "no longer
 * here", and neither alone is enough.
 *
 * ── Why a future exit date does NOT count ─────────────────────────────────────────────────
 *
 * An exit date that has not arrived yet is somebody serving notice. They are still coming in,
 * still need a shift assigned for the rest of the month, and still belong in the reports their
 * manager is using right now. Treating the presence of the field as the test — rather than the
 * date it holds — would remove them from the roster the moment HR recorded their resignation,
 * which is precisely the period their cover has to be planned.
 *
 * The comparison is a plain string compare on ISO dates, which orders correctly, and `todayIso`
 * is passed in rather than computed here: callers already hold the local-timezone date (see
 * CompOffService.todayIso, and why it is not the UTC one), and computing it per row would ask
 * the same question hundreds of times per render.
 */
export interface EmploymentRecord {
  dateOfExit?: string;
  enabled?: boolean;
}

export const hasLeftOrganisation = (user: EmploymentRecord, todayIso: string): boolean => {
  const exit = (user.dateOfExit || '').trim();
  if (exit && exit <= todayIso) return true;
  return user.enabled === false;
};

/** The complement, for the common case of filtering a list down to people still here. */
export const isCurrentEmployee = (user: EmploymentRecord, todayIso: string): boolean =>
  !hasLeftOrganisation(user, todayIso);

/**
 * Today in the VIEWER's timezone, as YYYY-MM-DD.
 *
 * Lives here because every caller of hasLeftOrganisation needs it and none of them should
 * compute it themselves — the obvious `new Date().toISOString().slice(0, 10)` is the UTC day,
 * which for anyone east of Greenwich is still YESTERDAY for the first hours of every morning.
 * An exit dated today would then not count as past until mid-morning, so somebody's last day
 * would end at a different hour depending on where the viewer was sitting.
 *
 * Same construction and the same reasoning as CompOffService.todayIso.
 */
export const todayIso = (asOf: Date = new Date()): string => {
  const year = asOf.getFullYear();
  const month = String(asOf.getMonth() + 1).padStart(2, '0');
  const day = String(asOf.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** The three states the User Management status filter offers. */
export type EmploymentStatusFilter = 'all' | 'active' | 'inactive';

/**
 * Narrow a list of people to those still here, those who have left, or everybody.
 *
 * Lives beside the rule rather than inside the screen so the two cannot drift: `active` and
 * `inactive` are defined as exact complements of isCurrentEmployee, which means nobody can fall
 * into neither group and nobody can appear in both. Written inline in a component, the tempting
 * shorthand for "inactive" is `!!user.dateOfExit` — and that is a different rule, one that counts
 * somebody with a FUTURE last day as already gone.
 */
export const filterByEmploymentStatus = <T extends EmploymentRecord>(
  users: T[],
  status: EmploymentStatusFilter,
  today: string
): T[] => {
  if (status === 'all') return users;
  return users.filter(user =>
    status === 'active' ? isCurrentEmployee(user, today) : hasLeftOrganisation(user, today)
  );
};

/** How many people fall in each group, for labelling the filter itself. */
export const countByEmploymentStatus = <T extends EmploymentRecord>(
  users: T[],
  today: string
): { all: number; active: number; inactive: number } => {
  const active = users.filter(user => isCurrentEmployee(user, today)).length;
  return { all: users.length, active, inactive: users.length - active };
};
